import { SESSION_IDS_CART_ATTRIBUTE_KEY } from "@redotech/redo-model/shopify/cart";
import { SessionIdsCartAttribute } from "@redotech/redo-model/user-session/session-ids-cart-attribute";
import { AllCookies, getCookie } from "@redotech/redo-web/utils/cookies";
import {
  getSessionStorageWithExpiry,
  setSessionStorageWithExpiry,
} from "@redotech/redo-web/utils/session-storage-wrapper";
import { ExtensionShopifyClient } from "@redotech/shopify-client/ajax";
import type { RedoShopifyRpcClient } from "@redotech/shopify-sdk/rpc/client";
import { conciergePresent } from "@redotech/shopify-sdk/rpc/schema/user-session/concierge-present";
import { isDef } from "@redotech/util/checks";
import { log } from "../monitoring/log";

const CONCIERGE_PRESENT_SESSION_CACHE_KEY = "redo.conciergePresentSessionCache";
/**
 * It will last 24 hours or less. If the session ID changes, the cache will be invalidated.
 */
const CONCIERGE_PRESENT_SESSION_CACHE_TTL_HOURS = 24;

interface ConciergePresentSessionCache {
  sessionId: string;
}

export async function sendConciergeLoadedEvent(
  rpcClient: RedoShopifyRpcClient,
) {
  const shopifySessionId = getCookie(AllCookies.ShopifySessionId);
  if (shopifySessionId === undefined) {
    throw new Error("Shopify session id is missing");
  }

  const cachedEventSent: ConciergePresentSessionCache | null =
    getSessionStorageWithExpiry(CONCIERGE_PRESENT_SESSION_CACHE_KEY);
  if (
    isDef(cachedEventSent) &&
    cachedEventSent.sessionId === shopifySessionId
  ) {
    return;
  }

  await rpcClient(conciergePresent, shopifySessionId);

  const cachedEvent: ConciergePresentSessionCache = {
    sessionId: shopifySessionId,
  };
  setSessionStorageWithExpiry(
    CONCIERGE_PRESENT_SESSION_CACHE_KEY,
    cachedEvent,
    CONCIERGE_PRESENT_SESSION_CACHE_TTL_HOURS,
  );
}

export async function addSessionIdToCart(
  extensionShopifyClient: ExtensionShopifyClient,
) {
  const shopifySessionId = getCookie(AllCookies.ShopifySessionId);
  if (shopifySessionId === undefined) {
    throw new Error("Shopify session id is missing");
  }

  const cart = await extensionShopifyClient.cartGet();
  const sessionIdsCartAttributeJson =
    cart.attributes?.[SESSION_IDS_CART_ATTRIBUTE_KEY];

  if (isDef(sessionIdsCartAttributeJson)) {
    try {
      const sessionIdsCartAttribute: SessionIdsCartAttribute = JSON.parse(
        sessionIdsCartAttributeJson,
      );
      if (sessionIdsCartAttribute.sessionIds.includes(shopifySessionId)) {
        return;
      }

      await addSessionIdsCartAttribute(extensionShopifyClient, [
        ...sessionIdsCartAttribute.sessionIds,
        shopifySessionId,
      ]);
      return;
    } catch (error) {
      log("Error parsing session ids cart attribute", error, {
        sessionIdsCartAttributeJson,
      });
    }
  }

  await addSessionIdsCartAttribute(extensionShopifyClient, [shopifySessionId]);
}

async function addSessionIdsCartAttribute(
  extensionShopifyClient: ExtensionShopifyClient,
  sessionIds: string[],
) {
  const sessionIdsCartAttribute: SessionIdsCartAttribute = { sessionIds };

  await extensionShopifyClient.updateCartAttribute({
    attributes: [
      {
        key: SESSION_IDS_CART_ATTRIBUTE_KEY,
        value: JSON.stringify(sessionIdsCartAttribute),
      },
    ],
  });
}
